import React, { useState, useEffect, useMemo } from 'react'
import 'form-pattern/dist/main.css'
import BreadCrumbs from '../../../components/common/BreadCrumbs/BreadCrumbs'
import TimeOffBalance from '../TimeOffBalance'
import { EmployeeInfo } from '../EmployeeInfo'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import Datepicker from '../../../components/common/DatePicker/Datepicker'
import { scrollToTopAndFocusAlert } from '../../../utils/scrollUtils'
import { formatHours } from '../../../utils/formatters'
import Table from '../../../components/common/Table/Table'
import Dropdown, { DropdownOption } from '../../../components/common/Dropdown'
import ValidationBanner from '../../../components/common/ValidationBanner'
import {
  validateChoiceRank,
  validateVacationBiddingEntries,
  validateDateGaps,
} from '../../../components/common/Validation/validationEngine'
import ColorLegendTooltip from '../../../components/common/ColorLegendTooltip'
import ValidationMessages from '../../../components/common/Validation/validationMessageConfig'
import { TextInputField } from '../../../components/common/FormFields'
import { ValidationErrorDisplay } from '../../../components/common/FormFields/ValidationErrorDisplay'
import Loader from '../../../components/common/Loader'
import Modal from '../../../components/common/Modal/Modal'
import { useProfileDisplay } from '../../../hooks/useProfileDisplay'
import { useDispatch, useSelector } from 'react-redux'
import ManualTimeOffBanner from '../../../components/common/ManualTimeOffBanner'
import PencilIcon from '../../../../public/assets/images/pencil-outline.svg'
import MinusCircleIcon from '../../../../public/assets/images/minus-circle-outline.svg'
import { useCreateTimeOffRequest } from '../../../hooks/useCreateTimeOffRequest'
import {
  convertMMDDYYYYToYYYYMMDD,
  formatManualRequestReceivedDate,
  isPastDate,
} from '../../../utils/date'
import { getOtherReqDetails } from '../../../store/slices/Requestor/createNewRequestSlice'
import { isVacBidDayInOtherRequestsChecked } from '../StatusViews/detail/constants'
import { getTimeOffTypeLabel } from '../../../utils/timeOffTypes'
import { RootState, AppDispatch } from '../../../store/store'
import { getTimeOffDetails } from '../../../store/slices/Requestor/timeOffDetailsSlice'
import { setRequestorActiveTab } from '../../../store/slices/Requestor/requestorTabSlice'
import ManualRequestedDate from '../../../components/common/ManualRequestedDate'
import { LabeledValue } from '../../../components/common/LabeledValue'

interface VacationBidEntry {
  id: string
  dates: string[]
  hoursPerDay: string | Record<string, string>
  choiceRank: string | Record<string, string>
  totalHours: number
  comments: string
}

const getDateRank = (
  bid: VacationBidEntry,
  dateStr: string
): string => {
  if (typeof bid.choiceRank === 'string') {
    return bid.choiceRank
  }

  return bid.choiceRank[dateStr] || ''
}

// Utility function to get hours for a specific date
const getDateHours = (bid: VacationBidEntry, dateStr: string): string => {
  if (typeof bid.hoursPerDay === 'string') return bid.hoursPerDay
  return bid.hoursPerDay[dateStr] || '8'
}

// Applies a single rank to every date across all bids so the whole request always carries one rank.
const applyRankToAllBids = (bids: VacationBidEntry[], rankValue: string): VacationBidEntry[] =>
  bids.map((bid) => {
    const dates = bid.dates.map(normalizeDate)
    return { ...bid, choiceRank: Object.fromEntries(dates.map((d) => [d, rankValue])) }
  })

// Normalize date strings to MM/DD/YYYY (zero-padded)
const normalizeDate = (s: string) => {
  const date = new Date(s)
  if (Number.isNaN(date.getTime())) return s
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const yyyy = date.getFullYear()
  return `${mm}/${dd}/${yyyy}`
}

/** Banner message shown when no choice/rank has been selected. */
const CHOICE_RANK_BANNER_MESSAGE = 'Choice/Rank is required'

/** Non-duration banner messages that originate from the hours-per-day field. */
const HOURS_BANNER_MESSAGES = new Set<string>([
  'Hours per day is required',
  'Hours must be a number',
])

/**
 * Identifies banner messages produced by the hours-per-day validation so they
 * can be cleared independently of other errors (e.g. the choice/rank error).
 *
 * @param msg - A banner message string.
 * @returns True when the message relates to the hours/duration rule.
 */
const isHoursBannerMessage = (msg: string): boolean =>
  msg === ValidationMessages.DURATION ||
  HOURS_BANNER_MESSAGES.has(msg) ||
  msg.toLowerCase().includes('duration')

/**
 * Identifies the choice/rank banner message so it can be cleared independently
 * of an unresolved hours error.
 *
 * @param msg - A banner message string.
 * @returns True when the message is the choice/rank requirement.
 */
const isChoiceRankBannerMessage = (msg: string): boolean =>
  msg === CHOICE_RANK_BANNER_MESSAGE

/**
 * Returns the banner messages with every entry matching `predicate` removed,
 * collapsing to `null` when nothing remains. Used so fixing one field only
 * clears its own message and leaves other unresolved errors visible.
 *
 * @param prev - The current banner messages (or null).
 * @param predicate - Returns true for messages that should be removed.
 * @returns The filtered messages, or null when empty.
 */
const withoutBannerMessages = (
  prev: string[] | null,
  predicate: (msg: string) => boolean
): string[] | null => {
  if (!prev) return prev
  const remaining = prev.filter((msg) => !predicate(msg))
  return remaining.length > 0 ? remaining : null
}

interface TableDisplayProps {
  vacationBids: VacationBidEntry[]
  onEdit: (id: string, dateStr: string) => void
  onRemove: (bidId: string, dateStr: string) => void
  otherReqData: Record<string, any[]> | null | undefined
  currentRequestId?: string
}

/**
 * Renders the "Day in Other Requests" column as a disabled checkbox
 * (matching legacy's read-only checked/unchecked checkbox), and falls back
 * to the plain cell value for every other column.
 */
const renderOverlapCheckboxCell = (row: any, col: { key: string }): React.ReactNode => {
  if (col.key !== 'hoursOtherRequests') return row[col.key]
  if (row._totalRow) return ''
  return (
    <input
      type="checkbox"
      className="table-cell-checkbox"
      checked={!!row.hoursOtherRequests}
      disabled
      readOnly
      aria-label="Day in other requests"
    />
  )
}

interface EditButtonProps {
  bidId: string
  dateStr: string
  onEdit: (id: string, dateStr: string) => void
}

const EditButton: React.FC<EditButtonProps> = ({ bidId, dateStr, onEdit }) => (
  <button
    className="flex items-center gap-2 bg-transparent border-0 p-0 cursor-pointer hover:opacity-80"
    type="button"
    aria-label="Edit"
    onClick={() => onEdit(bidId, dateStr)}
  >
    <img src={PencilIcon} alt="Edit" className="w-4 h-4" />
  </button>
)

interface RemoveButtonProps {
  bidId: string
  dateStr: string
  onRemove: (bidId: string, dateStr: string) => void
}

const RemoveButton: React.FC<RemoveButtonProps> = ({
  bidId,
  dateStr,
  onRemove,
}) => (
  <button
    className="flex items-center gap-2 bg-transparent border-0 p-0 cursor-pointer hover:opacity-80"
    type="button"
    aria-label="Remove"
    onClick={() => onRemove(bidId, dateStr)}
  >
    <img src={MinusCircleIcon} alt="Remove" className="w-4 h-4" />
  </button>
)
const VacationBidTableDisplay = ({
  vacationBids,
  onEdit,
  onRemove,
  otherReqData,
  currentRequestId,
}: TableDisplayProps) => {
  const currentRequestDetailId = (() => {
    if (!currentRequestId) return undefined
    const parsed = Number(currentRequestId)
    return Number.isNaN(parsed) ? undefined : parsed
  })()

  const getTableRows = () => {
    const dataRows: any[] = []
    // Safety net: if the same date ever ends up in more than one bid (e.g.
    // a "new details" entry created alongside an already-committed date's
    // original entry), only the first occurrence is rendered. vacationBids
    // preserves creation order, so the original/old bid is always seen
    // before any later-appended bid — this keeps the old date's old
    // details and drops the duplicate instead of showing both.
    const seenDates = new Set<string>()

    // Create a row for each date in each bid
   vacationBids.forEach((bid) => {
  [...bid.dates]
    .sort(
      (a, b) =>
        new Date(a).getTime() - new Date(b).getTime()
    )
    .forEach((dateStr) => {
      const normalizedDateStr = normalizeDate(dateStr)
      if (seenDates.has(normalizedDateStr)) return
      seenDates.add(normalizedDateStr)

      const dateObj = new Date(dateStr)
      const dayName = dateObj.toLocaleDateString('en-US', {
        weekday: 'long',
      })
      const formattedDate = dateObj.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      })

      const dateHours = getDateHours(bid, dateStr)

      dataRows.push({
        id: `${bid.id}-${dateStr}`,
        bidId: bid.id,
        requestType: 'Vacation Bidding',
        date: formattedDate,
        day: dayName,
        timesRequested: 'Full Day',
        choiceRank: getDateRank(bid, dateStr),
        hours: dateHours,
        hoursOtherRequests: isVacBidDayInOtherRequestsChecked(
          otherReqData,
          dateStr,
          currentRequestDetailId
        ),
        edit: (
          <EditButton
            bidId={bid.id}
            dateStr={dateStr}
            onEdit={onEdit}
          />
        ),
        remove: (
          <RemoveButton
            bidId={bid.id}
            dateStr={dateStr}
            onRemove={onRemove}
          />
        ),
        _rowStyle: {
          background: 'var(--Color-Graphite-Light, #F4F5F6)',
        },
      })
    })
})
    // Rows are built per-bid (each bid's own dates sorted ascending), but
    // bids themselves are appended in creation order — e.g. a date added
    // later via "Add date to request" can be earlier than dates already in
    // the table. Sort the combined rows by date so the review table is
    // always in ascending date order regardless of bid order.
    dataRows.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

    // Calculate totals
    const totalHours = vacationBids.reduce(
      (sum, bid) => sum + bid.totalHours,
      0
    )
    // Add totals row
    const totalsRow = {
      id: 'totals-row',
      requestType: 'Total Hours',
      date: '',
      day: '',
      timesRequested: '',
      choiceRank: '',
      hours: formatHours(totalHours),
      hoursOtherRequests: false,
      edit: '',
      remove: '',
      _totalRow: true,
      _rowStyle: {
        background: 'var(--Color-Graphite-Dark, #D3D6DC)',
        border: '0 solid var(--Color-Slate, #878F9B)',
      },
    }

    return [...dataRows, totalsRow]
  }

  const columns = [
    {
      key: 'requestType',
      label: 'Request Type',
      className: 'whitespace-nowrap',
    },
    { key: 'date', label: 'Date', className: 'whitespace-nowrap' },
    { key: 'day', label: 'Day', className: 'whitespace-nowrap' },
    { key: 'timesRequested', label: 'Time', className: 'whitespace-nowrap' },
    { key: 'choiceRank', label: 'Rank', className: 'whitespace-nowrap' },
    { key: 'hours', label: 'Requested Hours', className: 'w-30' },
    {
      key: 'hoursOtherRequests',
      label: 'Day in Other Requests',
      className: 'w-40',
    },
    { key: 'edit', label: 'Edit', className: 'whitespace-nowrap' },
    { key: 'remove', label: 'Remove', className: 'whitespace-nowrap' },
  ]

  const rows = getTableRows()

  return (
    <Table
      columns={columns}
      rows={rows}
      renderCell={renderOverlapCheckboxCell}
      showPagination={false}
      isSorting={true}
      sortColumn='date'
    />
  )
}

interface EditModeHeaderProps {
  selectedDates: string[]
  newlyAddedDates?: string[]
}

const EditModeHeader: React.FC<EditModeHeaderProps> = ({
  selectedDates,
  newlyAddedDates,
}) => (
  <>
    <h2 className="h2 text-lg md:text-xl ml-0">Edit Vacation Bid</h2>
    <p className="p3 -book font-bold! pt-2 text-sm md:text-base">
      Requesting Time Off Hours For:
    </p>
    {selectedDates.length > 0 && (
      <ul className="list-disc ml-5!">
        {selectedDates.map((date) => (
          <li key={date} className="p3 -book text-sm md:text-base">
            {date}
          </li>
        ))}
      </ul>
    )}

    {(newlyAddedDates?.length ?? 0) > 0 && (
      <>
        <p className="pp3 -book font-bold! pt-2 text-sm md:text-base">Currently Adding:</p>

        <ul className="list-disc ml-5">
          {newlyAddedDates?.map((date) => (
            <li key={date}>{date}</li>
          ))}
        </ul>
      </>
    )}
  </>
)

interface AddModeHeaderProps {
  sortedDates: string[]
}

const AddModeHeader: React.FC<AddModeHeaderProps> = ({ sortedDates }) => (
  <>
    <h2 className="h2 text-lg md:text-xl m-0">Request Vacation Bid</h2>
    <p className="p3 -book font-bold! pt-2 text-sm md:text-base">
      Requesting Time Off Hours For:
    </p>
    {sortedDates.length > 0 && (
      <ul className="list-disc ml-5!">
        {sortedDates.map((date) => (
          <li key={date} className="p3 -book text-sm md:text-base">
            {date}
          </li>
        ))}
      </ul>
    )}
  </>
)

interface DurationInputProps {
  hoursPerDay: string
  onHoursChange: (value: string) => void
  error?: string
  onErrorClear?: () => void
}

const validateHours = (hours: string): string | null => {
  if (!hours) return 'Hours per day is required'
  const numHours = Number.parseFloat(hours)
  if (Number.isNaN(numHours)) return 'Hours must be a number'
  if (numHours < 0.25 || numHours > 16) return 'Revise'
  return null
}



const DurationInput: React.FC<DurationInputProps> = ({
  hoursPerDay,
  onHoursChange,
  error,
  onErrorClear,
}) => (
  <div className="pt-4 mt-6!">
    <div className="hidden md:grid grid-cols-[230px_200px] gap-6 pb-3 border-b border-gray-300 w-[700px]">
        <p className="p3 -medium text-sm md:text-base ">Hours Per Day</p>
        <p className="p3 -medium text-sm md:text-base">Time Off Type</p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-[230px_200px] gap-2 md:gap-6 items-start md:items-center pt-8! mb-8 pb-6">
      <div className="w-full md:w-[230px]">
        <TextInputField
          id="hours-input"
          name="hoursPerDay"
          label="Hours Per Day"
          type="text"
          placeholder=""
          value={hoursPerDay}
          onChange={(e) => {
            const nextValue = e.target.value

            // Allow only numbers and one decimal point
            if (!/^[\d.]*$/.test(nextValue) || (nextValue.match(/\./g) || []).length > 1) {
              return
            }

            onHoursChange(nextValue)
            // Keep the hours error visible until a VALID value is entered —
            // clearing on the first keystroke made the banner disappear before
            // the user had actually corrected the hours.
            if (error && onErrorClear && !validateHours(nextValue))
              onErrorClear()
          }}
          error={error}
          customStyles={{ width: '85%' }}
        />
      </div>
      <div className="w-full md:w-[200px]">
        <p className="p3 -book text-sm md:text-base">
          Vacation Bidding<span className="text-red-500">*</span>
        </p>
      </div>
    </div>
  </div>
)

interface ChoiceRankProps {
  selectedRank: DropdownOption | null
  onSelectRank: (option: DropdownOption) => void
  dropdownOptions: DropdownOption[]
  error?: string
  onErrorClear?: () => void
}

const ChoiceRankSelector: React.FC<ChoiceRankProps> = ({
  selectedRank,
  onSelectRank,
  dropdownOptions,
  error,
  onErrorClear,
}) => (
  <div className="mt-3 w-full md:w-auto lg:max-w-xs">
    <label htmlFor="choice-rank-dropdown" className="ds-dropdown__label">
      <span className="ds-dropdown__label-text">
        Choice Rank<sup className="align-super text-red-500">*</sup>
      </span>
    </label>
    <div>
      <Dropdown
        selectedValue={selectedRank || undefined}
        options={dropdownOptions}
        onSelect={(option) => {
          onSelectRank(option)
          if (error && onErrorClear) onErrorClear()
        }}
        placeholder="Select a choice rank"
        menuClassName="max-w-full max-h-[20rem]!"
      />
      {error && (
        <ValidationErrorDisplay message={error} id="choice-rank-error" />
      )}
    </div>
  </div>
)

interface ActionButtonsProps {
  editMode: boolean
  onBack?: () => void
  onCancel: () => void
  onAdd?: () => void
  onUpdate?: () => void
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  editMode,
  onBack,
  onCancel,
  onAdd,
  onUpdate,
}) => (
  <div className="flex flex-col-reverse md:flex-row items-stretch md:items-center gap-3 mt-8! pb-6">
    {editMode ? (
      <>
        <button
          type="button"
          className="button -light block w-full md:w-auto"
          onClick={onBack}
        >
          Back
        </button>
        <button
          type="button"
          className="button -light block w-full md:w-auto"
          onClick={onCancel}
        >
          Cancel Request
        </button>
        <button
          type="button"
          className="button -primary -light block w-full md:w-auto"
          onClick={onUpdate}
        >
          Update
        </button>
      </>
    ) : (
      <>
        <button
          type="button"
          className="button -light block w-full md:w-auto"
          onClick={onCancel}
        >
          Cancel Request
        </button>
        <button
          type="button"
          className="button -primary -light block w-full md:w-auto"
          onClick={onAdd}
        >
          Next: Review Request
        </button>
      </>
    )}
  </div>
)

interface FormContentProps {
  editMode: boolean
  selectedDates: string[]
  sortedDates: string[]
  hoursPerDay: string
  totalHours: number
  selectedRank: DropdownOption | null
  dropdownOptions: DropdownOption[]
  onHoursChange: (value: string) => void
  onSelectRank: (option: DropdownOption) => void
  onAdd: () => void
  onBack: () => void
  onCancel: () => void
  onUpdate: () => void
  hoursError?: string
  onHoursErrorClear?: () => void
  choiceRankError?: string
  onChoiceRankErrorClear?: () => void
  newlyAddedEditDates: string[]
  editOriginalDates: string[]
  editingBidId: string | null
}

const FormContent: React.FC<FormContentProps> = ({
  editMode,
  selectedDates,
  sortedDates,
  hoursPerDay,
  totalHours,
  selectedRank,
  dropdownOptions,
  onHoursChange,
  onSelectRank,
  onAdd,
  onBack,
  onCancel,
  onUpdate,
  hoursError,
  onHoursErrorClear,
  choiceRankError,
  onChoiceRankErrorClear,
  newlyAddedEditDates,
  editOriginalDates,
  editingBidId,
}) => {
  // "Add date to request" bulk-add flow: editOriginalDates is pre-seeded with
  // every already-committed date (so they stay visible on the calendar), which
  // would otherwise make selectedDates non-empty immediately and pop the form
  // open before the user has picked anything new. In that flow specifically,
  // gate on newlyAddedEditDates instead so the form only appears once the
  // user actually clicks a date on the calendar.
  const isBulkAddWithoutBid =
    editMode && !editingBidId && editOriginalDates.length > 0
  const hasNoSelection = isBulkAddWithoutBid
    ? newlyAddedEditDates.length === 0
    : selectedDates.length === 0

  if (hasNoSelection) {
    return (
      <p className="p3 -medium pt-2 tablet:pt-4 mobile:pt-2">
        Select a date on the Calendar to add time to your request
      </p>
    )
  }

  return (
    <>
      {editMode ? (
        <EditModeHeader
          selectedDates={editOriginalDates.map(normalizeDate)}
          newlyAddedDates={newlyAddedEditDates}
        />
      ) : (
        <AddModeHeader sortedDates={sortedDates} />
      )}


      <p className="p3 -medium pt-4 text-sm md:text-base">Duration</p>
      <div className="flex flex-col gap-3 pt-2">
        <label
          htmlFor="radio-duration-full"
          className="inline-flex items-center gap-3 cursor-pointer"
        >
          <input
            id="radio-duration-full"
            name="radio-duration"
            type="radio"
            value="Full Day"
            defaultChecked
            className="radio-button"
          />
          <span className="p3 -medium text-sm md:text-base">Full Day</span>
        </label>
      </div>

      <DurationInput
        hoursPerDay={hoursPerDay}
        onHoursChange={onHoursChange}
        error={hoursError}
        onErrorClear={onHoursErrorClear}
      />

      <hr className="border-t border-gray-300 max-w-[704px] mt-8! mb-0!" />
      <p className="p2 -book text-sm md:text-base mt-4! max-w-[704px]">
        <span className="text-red-500">*</span>Vacation Bidding Requests do not
        update your Vacation (PTO/ETO) Available Balance until they have been
        approved.
      </p>

      <div className="mt-10!">
        <ChoiceRankSelector
          selectedRank={selectedRank}
          onSelectRank={onSelectRank}
          dropdownOptions={dropdownOptions}
          error={choiceRankError}
          onErrorClear={onChoiceRankErrorClear}
        />
      </div>

      <p className="p2 -book text-sm md:text-base">
        {formatHours(Number.parseFloat(hoursPerDay) || 0)} Hours requested per
        day
      </p>
      <p className="p2 -book text-sm md:text-base">
        {formatHours(totalHours)} Hours Total
      </p>

      <ActionButtons
        editMode={editMode}
        onBack={onBack}
        onCancel={onCancel}
        onAdd={onAdd}
        onUpdate={onUpdate}
      />
    </>
  )
}

interface HistoryTableSectionProps {
  vacationBids: VacationBidEntry[]
  comments: string
  onEditBid: (id: string, dateStr: string) => void
  onRemoveBid: (bidId: string, dateStr: string) => void
  onCommentsChange: (value: string) => void
  onCancel: () => void
  onSaveDraft: () => void
  onSubmit: () => void
  isSubmitting?: boolean
  activeSubmitAction?: SubmitAction
  adminReceivedDate?: string
  setAdminReceivedDate?: (v: string) => void
  adminReceivedDateError?: string
  onAdminDateError?: (error: string) => void
  isAdminMode?: boolean
  onAddMoreDates: () => void 
  otherReqData: Record<string, any[]> | null | undefined
}

const HistoryTableSection: React.FC<HistoryTableSectionProps> = ({
  vacationBids,
  comments,
  onEditBid,
  onRemoveBid,
  onCommentsChange,
  onCancel,
  onSaveDraft,
  onSubmit,
  isSubmitting = false,
  activeSubmitAction = null,
  adminReceivedDate,
  setAdminReceivedDate,
  isAdminMode = false,
  onAddMoreDates,
  adminReceivedDateError,
  onAdminDateError,
  otherReqData,
}) => (
  <>
    <h2 className="h2 m-0 mb-4 text-lg md:text-xl">Review Your Vacation Bid</h2>
    <p className="p3 -book">
      To add a date, select the Add date to request button. To edit or remove a date, you may use the table below.
    </p>
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            className="button -secondary block w-full! desktop:w-auto!"
            onClick={onAddMoreDates}
          >
            Add date to request
          </button>
        </div>
    <div className="overflow-x-auto mt-8">
      <VacationBidTableDisplay
        vacationBids={vacationBids}
        onEdit={onEditBid}
        onRemove={onRemoveBid}
        otherReqData={otherReqData}
      />
    </div>

    <div className="form-group margin-top-2u mt-8 max-w-2xl">
      <label
        id="comments-label"
        className="p4 -medium"
        htmlFor="comments-textarea"
      >
        Comments
      </label>
      <div className="textarea-wrapper w-full relative">
        <textarea
          autoComplete="on"
          id="comments-textarea"
          name="comments"
          aria-labelledby="comments-label comments-count"
          className="text-area_word-count text-area_global -book text-base! text-[#0D1C3D]! w-full!"
          data-show-lines="3"
          rows={3}
          maxLength={1000}
          onChange={(e) => onCommentsChange(e.target.value)}
          value={comments}
        />
        <div className="character-count">
          <span
            id="comments-count"
            aria-live="polite"
            aria-atomic="true"
            className="count-area text-input-helper"
            data-description=" of 1000 characters left"
            data-full-text="No more characters available, 1000 of 1000 characters left"
          >
            {1000 - comments.length} of 1000 characters left
          </span>
        </div>
      </div>
    </div>
    {isAdminMode && (
      <div className="mt-5!">
        <ManualRequestedDate
          name="manual-requested-date"
          label="Manual Requested Date"
          value={adminReceivedDate}
          onChange={(v) => {
            setAdminReceivedDate?.(v)
            if (!v.trim()) {
              onAdminDateError?.('')
              return
            }
            const parsed = new Date(v)
            const today = new Date()
            today.setHours(23, 59, 59, 999)
            if (!Number.isNaN(parsed.getTime()) && parsed > today) {
              onAdminDateError?.('Future dates are not allowed for Date Manual Request Received.')
            } else {
              onAdminDateError?.('')
            }
          }}
          required={true}
          disableFutureDates={true}
          error={adminReceivedDateError}
        />
        <LabeledValue
          label="Submitted by Requestor Proxy"
          value={(() => {
            const manualEmployee = JSON.parse(
              sessionStorage.getItem('manualTimeOffEmployee') || '{}'
            )
            return `${manualEmployee?.admin?.adminName || 'Employee'} (${manualEmployee?.admin?.adminNuid || 'Unknown'})`
          })()}
        />
      </div>
    )}
    <div className="flex flex-col-reverse lg:mt-8! md:flex-row items-stretch md:items-center gap-3 mt-8 pb-6">
      <button
        type="button"
        className="button -light block w-full md:w-auto"
        onClick={onCancel}
        disabled={isSubmitting}
      >
        Cancel Request
      </button>
      <button
        type="button"
        className="button -light block w-full md:w-auto"
        onClick={onSaveDraft}
        disabled={vacationBids.length === 0 || isSubmitting}
      >
        {isSubmitting && activeSubmitAction === 'save'
          ? 'Saving...'
          : 'Save as Draft'}
      </button>
      <button
        type="button"
        className="button -primary -light block w-full md:w-auto"
        onClick={onSubmit}
        disabled={vacationBids.length === 0 || isSubmitting}
      >
        {isSubmitting && activeSubmitAction === 'submit'
          ? 'Submitting...'
          : 'Submit Vacation Bid'}
      </button>
    </div>
    {isSubmitting && <Loader />}
  </>
)

/**
 * Renders the vacation-bid request creation and review flow.
 * @returns The vacation-bid request page.
 */
const CreateVacationBidRequest: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const otherReqData = useSelector(
    (state: RootState) => state.otherReqDetails.data
  )
  const { submitVacationBid } = useCreateTimeOffRequest()
  const { userInfo, userNuid, primaryApproverInfo } = useProfileDisplay()

  const [selectedDates, setSelectedDates] = useState<string[]>([])
  const [hoursPerDay, setHoursPerDay] = useState<string>('0')
  const [vacationBids, setVacationBids] = useState<VacationBidEntry[]>([])
  const [showHistoryTable, setShowHistoryTable] = useState<boolean>(false)
  // Admin-only manual request received date
  const [adminReceivedDate, setAdminReceivedDate] = useState<string>('')
  const [adminReceivedDateError, setAdminReceivedDateError] = useState<string>('')
  const [editMode, setEditMode] = useState<boolean>(false)
  const [editDate, setEditDate] = useState<string | null>(null)
  const [editingBidId, setEditingBidId] = useState<string | null>(null)
  const [originalEditingBid, setOriginalEditingBid] =
    useState<VacationBidEntry | null>(null)
  const [comments, setComments] = useState<string>('')
  const [selectedRank, setSelectedRank] = useState<DropdownOption | null>(null)
  const [lastChoiceRank, setLastChoiceRank] = useState<DropdownOption | null>(
    null
  )
  const [bannerMessages, setBannerMessages] = useState<string[] | null>(null)
  const [hoursError, setHoursError] = useState<string>('')
  const [choiceRankError, setChoiceRankError] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  // Tracks which button (Save as Draft vs Submit) triggered the current
  // submission so only that button shows its loading label — otherwise
  // both buttons would read "Submitting..." off the single isSubmitting flag.
  const [activeSubmitAction, setActiveSubmitAction] =
    useState<SubmitAction>(null)
  // Track viewed month/year for calendar navigation
  const [viewedMonth, setViewedMonth] = useState(() => new Date().getMonth())
  const [viewedYear, setViewedYear] = useState(() => new Date().getFullYear())
  const [editOriginalDates, setEditOriginalDates] = useState<string[]>([])
  const [newlyAddedEditDates, setNewlyAddedEditDates] = useState<string[]>([])

  // Fetch other request details when month/year changes
  useEffect(() => {
    dispatch(getOtherReqDetails({ month: viewedMonth, year: viewedYear }))
  }, [viewedMonth, viewedYear, dispatch])
  // Show all dates from other request data (no filtering - prepopulate everything)
  const blockedOtherReqData = otherReqData

  // Convert YYYY-MM-DD keys from API → MM/DD/YYYY for the Datepicker
  const otherRequestDates = useMemo(() => {
    return Object.keys(blockedOtherReqData).map((dateStr) => {
      const [year, month, day] = dateStr.split('-')
      return `${month}/${day}/${year}`
    })
  }, [blockedOtherReqData])

    // Fetch other request details when month/year changes (including on mount)
    React.useEffect(() => {
      dispatch(getOtherReqDetails({ month: viewedMonth, year: viewedYear }))
    }, [viewedMonth, viewedYear, dispatch])

  /**
   * Build allocations array from first detail's allocations
   */
  const buildFirstAllocation = (
    detail: any
  ): Array<{ typeLabel: string; hours: number }> => {
    if (!detail.timeOffAllocations || detail.timeOffAllocations.length === 0)
      return []
    const firstAlloc = detail.timeOffAllocations[0]
    return [
      {
        typeLabel: getTimeOffTypeLabel(firstAlloc.toTypeCd),
        hours: Number(firstAlloc.hours) || 0,
      },
    ]
  }

  /**
   * Build allocations array from detail's all allocations
   */
  const buildAllocations = (
    detail: any
  ): Array<{ typeLabel: string; hours: number }> => {
    return detail.timeOffAllocations.map((alloc: any) => ({
      typeLabel: getTimeOffTypeLabel(alloc.toTypeCd),
      hours: Number(alloc.hours) || 0,
    }))
  }

  /**
   * Calculate total hours from allocations
   */
  const calculateTotalHours = (
    allocations: Array<{ typeLabel: string; hours: number }>
  ): number => {
    return allocations.reduce((sum: number, a: any) => sum + a.hours, 0)
  }

  /**
   * Build individual request entry
   */
  const buildIndividualRequest = (detail: any) => ({
    startTime: detail.startTime,
    endTime: detail.endTime,
    allocations: buildAllocations(detail),
    totalHours: calculateTotalHours(buildAllocations(detail)),
  })

  /**
   * Build individual requests array for tooltip
   */
  const buildIndividualRequests = (
    durationLabel: string,
    detailsArray: any[],
    firstDetail: any
  ) => {
    if (durationLabel === 'Partial Day' && detailsArray.length > 1) {
      return detailsArray.map(buildIndividualRequest)
    }
    if (
      durationLabel === 'Partial Day' &&
      firstDetail.timeOffAllocations.length > 1
    ) {
      return [buildIndividualRequest(firstDetail)]
    }
    return undefined
  }

  // Transform API data to tooltip format (use filtered data)
  const otherRequestDetailsForTooltip = useMemo(() => {
    const result: Record<
      string,
      {
        durationLabel: string
        date: string
        allocations: Array<{ typeLabel: string; hours: number }>
        totalHours: number
        startTime?: string
        endTime?: string
        hasMultipleRequests?: boolean
        individualRequests?: any[]
      }
    > = {}
    Object.entries(blockedOtherReqData).forEach(([dateKey, detailsArray]) => {
      if (!Array.isArray(detailsArray) || detailsArray.length === 0) return


      const [year, month, day] = dateKey.split('-')
      const formattedDate = `${month}/${day}/${year}`

      const firstDetail = detailsArray[0]
      const durationLabel =
        firstDetail.durationFlag === 'F' ? 'Full Day' : 'Partial Day'

      // Show ONLY first allocation in tooltip as summary
      const tooltipAllocations = buildFirstAllocation(firstDetail)
      const tooltipTotalHours = calculateTotalHours(tooltipAllocations)

      const tooltipEntry: any = {
        durationLabel,
        date: formattedDate,
        allocations: tooltipAllocations,
        totalHours: tooltipTotalHours,
        hasMultipleRequests:
          detailsArray.length > 1 || firstDetail.timeOffAllocations.length > 1,
        status: firstDetail.status,
        otherRequests:firstDetail.otherRequests
      }
      if (firstDetail.startTime) tooltipEntry.startTime = firstDetail.startTime
      if (firstDetail.endTime) tooltipEntry.endTime = firstDetail.endTime

      tooltipEntry.individualRequests = buildIndividualRequests(
        durationLabel,
        detailsArray,
        firstDetail
      )

      result[dateKey] = tooltipEntry
    })
    return result
  }, [blockedOtherReqData])
    const lastCalendarView = React.useRef({
  month: new Date().getMonth(),
  year: new Date().getFullYear(),
})

  // Handle month/year change from calendar navigation
 const handleMonthYearChange = (month: number, year: number) => {
  setViewedMonth(month)
  setViewedYear(year)

  lastCalendarView.current = {
    month,
    year,
  }
}
  const { mode } = useParams<{ mode?: string }>()
  const location = useLocation()
  const isAdminMode =
    mode === 'admin' || location?.pathname?.startsWith?.('/admin') || false

  const handleBreadcrumbPrevious = () => {
    const basePath = mode ? `/${mode}/requestor` : '/requestor'
    // Dispatch Redux action to set active tab to 3 (Vacation Bidding)
    dispatch(setRequestorActiveTab(3))
    navigate(basePath, { state: { activeTab: 3 } })
  }

const handleAddMoreDates = () => {
  // "Add date to request" adds brand new dates alongside the existing bids —
  // it is not tied to any single bid, so editingBidId stays null. The
  // already-committed dates are seeded as editOriginalDates so that any date
  // the user newly selects is recognised as "new" by
  // handleVacationBidEditSelectionChange and surfaced under "Currently Adding".
  const allCommittedDates = vacationBids.flatMap((bid) => bid.dates)

  setShowHistoryTable(false)
  setEditMode(true)
  setEditDate(null)
  setEditingBidId(null)
  setOriginalEditingBid(null)
  setEditOriginalDates(allCommittedDates)
  setSelectedDates(allCommittedDates)
  setNewlyAddedEditDates([])
  setHoursPerDay('0')
  // Preselect the Choice Rank dropdown with whatever rank the request
  // already carries, sourced from the committed bid data itself (not just
  // session state) — lastChoiceRank is only set once the user has touched
  // the dropdown in this session, so on a freshly loaded/edited request it
  // would still be null and leave the dropdown blank. Falling back to the
  // first existing bid's rank keeps it populated either way, so the merge
  // logic in handleUpdateBid finds a match instead of spinning up a second
  // bid with a different rank for the newly added dates.
  const primaryBid = vacationBids[0]
  let existingRankValue: string | undefined
  if (primaryBid) {
    if (typeof primaryBid.choiceRank === 'string') {
      existingRankValue = primaryBid.choiceRank
    } else {
      existingRankValue = Object.values(primaryBid.choiceRank)[0]
    }
  }
  const rankToPreselect =
    dropdownOptions.find((opt) => opt.value === existingRankValue) ||
    lastChoiceRank
  if (rankToPreselect) setSelectedRank(rankToPreselect)
  setHoursError('')
  setChoiceRankError('')
  setBannerMessages(null)
}
  // Cancel/Reset the form - clears all entered data
  const handleCancelBid = () => {
    setSelectedDates([])
    setHoursPerDay('0')
    setVacationBids([])
    setShowHistoryTable(false)
    setEditMode(false)
    setEditDate(null)
    setEditingBidId(null)
    setOriginalEditingBid(null)
    setComments('')
    setSelectedRank(null)
    setLastChoiceRank(null)
    setBannerMessages(null)
    setHoursError('')
    setChoiceRankError('')
  }

  // Submit all vacation bids to the API
  const handleSubmitAllBids = async () => {
    setActiveSubmitAction('submit')
    await submitAllBids('SUBMITTED')
  }

  // Save all vacation bids as DRAFT
  const handleSaveDraftAllBids = async () => {
    setActiveSubmitAction('save')
    await submitAllBids('DRAFT')
  }


  // Shared helper to submit or save as draft depending on status.
  // Always merges every VacationBidEntry into ONE API call so the review
  // table and the submitted payload are always identical.
  const submitAllBids = async (status: 'SUBMITTED' | 'DRAFT') => {
    if (vacationBids.length === 0) return

    // Check for date gap exceeding 8 days on Save as Draft / Submit — same
    // check, same date set (existing bids ∪ in-progress selectedDates), as
    // "Next: Review Request" / "Update" above, so a gap that would have been
    // blocked there is also blocked here before the API call is made.
    const allDatesForGapCheck = [
      ...vacationBids.flatMap((bid) => bid.dates),
      ...selectedDates,
    ]
    const submitAllBidsGapError = validateDateGaps(allDatesForGapCheck)
    if (submitAllBidsGapError) {
      setBannerMessages([submitAllBidsGapError])
      scrollToTopAndFocusAlert()
      return
    }

    if (isAdminMode) {
      if (!adminReceivedDate.trim()) {
        setAdminReceivedDateError('Required')
        setBannerMessages(['Date Manual Request Received is required'])
        scrollToTopAndFocusAlert()
        return
      }
      if (adminReceivedDateError) {
        setBannerMessages([adminReceivedDateError])
        scrollToTopAndFocusAlert()
        return
      }
    }
    setAdminReceivedDateError('')

    // Collect every date from every bid — deduplicated and normalized.
    const allDates = Array.from(
      new Set(vacationBids.flatMap((bid) => bid.dates.map(normalizeDate)))
    )
    const allApiDates = allDates.map(convertMMDDYYYYToYYYYMMDD)

    // Build per-date hours from whichever bid owns each date.
    const perDateHoursValues: Record<
      string,
      { vacation: string; float: string; flexible: string; other: string }
    > = {}
    vacationBids.forEach((bid) => {
      bid.dates.forEach((dateStr) => {
        const normalized = normalizeDate(dateStr)
        const apiDateStr = convertMMDDYYYYToYYYYMMDD(normalized)
        const hours =
          typeof bid.hoursPerDay === 'string'
            ? bid.hoursPerDay
            : bid.hoursPerDay[normalized] ||
              bid.hoursPerDay[dateStr] ||
              '8'
        perDateHoursValues[apiDateStr] = {
          vacation: hours,
          float: '0',
          flexible: '0',
          other: '0',
        }
      })
    })

    const primaryBid = vacationBids[0]
    const primaryHours =
      typeof primaryBid.hoursPerDay === 'string' ? primaryBid.hoursPerDay : '8'
    const torRank =
      typeof primaryBid.choiceRank === 'string'
        ? Number.parseInt(primaryBid.choiceRank || '1', 10)
        : Number.parseInt(Object.values(primaryBid.choiceRank)[0] || '1', 10)

    setIsSubmitting(true)
    try {
      const result = await submitVacationBid(
        {
          selectedDates: allApiDates,
          hoursValues: {
            vacation: primaryHours,
            float: '0',
            flexible: '0',
            other: '0',
          },
          duration: 'Full Day',
          comments,
          torRank,
          perDateHoursValues,
          manualReqRcvdDate: isAdminMode
            ? formatManualRequestReceivedDate(adminReceivedDate)
            : undefined,
        },
        status
      )
      if (result.success) {
        await navigateAfterSuccess(status, [result])
      } else {
        setBannerMessages([String(result.error)])
        scrollToTopAndFocusAlert()
      }
    } catch (error: any) {
      setBannerMessages([
        error.message ||
          (status === 'SUBMITTED'
            ? 'Failed to submit vacation bids'
            : 'Failed to save vacation bids as draft'),
      ])
      scrollToTopAndFocusAlert()
    } finally {
      setIsSubmitting(false)
      setActiveSubmitAction(null)
    }
  }

  // Navigation helper extracted to reduce cognitive complexity of submitAllBids
  const navigateAfterSuccess = async (
    status: 'SUBMITTED' | 'DRAFT',
    results: any[]
  ) => {
    const firstId = results[0]?.timeOffRequestId || results[0]?.toReqId || ''
    const adminPrefix = isAdminMode ? '/admin' : ''
    if (status === 'SUBMITTED') {
      if (firstId) {
        try {
          const detailsResult = await dispatch(
            getTimeOffDetails(firstId) as any
          )
          if (detailsResult?.meta?.requestStatus === 'fulfilled') {
            const payload = detailsResult.payload
            navigate(`${adminPrefix}/requestor/vacation-bid`, {
              state: {
                showSubmittedBanner: true,
                vacationBids,
                timeOffDetails: payload,
              },
            })
            return
          }
        } catch (e) {
          console.error(
            'Failed to fetch time off details for submitted request',
            e
          )
        }
      }

      navigate(`${adminPrefix}/requestor/vacation-bid`, {
        state: { showSubmittedBanner: true, vacationBids },
      })
      return
    }

    // For drafts we don't need to fetch full details before navigating.
    // Navigate directly to the status page when we have an id.
    if (firstId) {
      navigate(`${adminPrefix}/requestor/status/${firstId}`)
      return
    }

    navigate(`${adminPrefix}/requestor/vacation-bid`, {
      state: { showSavedDraftBanner: true, vacationBids },
    })
  }

  const handleDateChange = (_name: string, value: string | string[]) => {
    let newSelected: string[] = []
    if (Array.isArray(value)) newSelected = value
    else if (value) newSelected = [value]

    // Filter out partial day dates (not applicable for vacation bidding)
    newSelected = newSelected.filter((dateStr: string) => {
      const apiKey = convertMMDDYYYYToYYYYMMDD(dateStr)
      const tooltipEntry = otherRequestDetailsForTooltip[apiKey]
      return tooltipEntry?.durationLabel !== 'Partial Day'
    })

    // Normalize incoming selections
    newSelected = newSelected.map(normalizeDate)

    // Clear errors whenever dates change
    setHoursError('')
    setChoiceRankError('')
    setBannerMessages(null)

    if (handleVacationBidEditSelectionChange(newSelected)) return
    if (handleVacationBidHistorySelectionChange(newSelected)) return
    if (handleVacationBidFreshCommittedSelectionChange(newSelected)) return

    setSelectedDates(newSelected)
  }

  /** Handles click on a date from history table view. Returns true if handled. */
  const handleHistoryTableDateClick = (
    normalizedClickedDate: string,
    containingBid: VacationBidEntry | undefined,
    clickedDate: string
  ): boolean => {
    if (!showHistoryTable) return false
    if (containingBid) {
      handleEditBid(containingBid.id, clickedDate)
    } else {
      setShowHistoryTable(false)
      setEditMode(false)
      setEditDate(null)
      setEditingBidId(null)
      setOriginalEditingBid(null)
      setSelectedDates([normalizedClickedDate])
      resetFreshSelectionState()
      setHoursError('')
      setChoiceRankError('')
      setBannerMessages(null)
    }
    return true
  }

  /** Checks if date is On Hold and handles accordingly. Returns true if handled. */
  const handleOnHoldDateClick = (clickedDate: string, apiKey: string): boolean => {
    const detailsArray = otherReqData[apiKey]
    const otherDetail = Array.isArray(detailsArray) && detailsArray.length > 0 ? detailsArray[0] : null
    const isOnHold = otherDetail?.status === 'SUBMITTED_ONHOLD' || otherDetail?.status === 'PRTSUBMITTED_ONHOLD'
    if (!isOnHold) return false

    const selectedDate = new Date(clickedDate)
    selectedDate.setHours(0, 0, 0, 0)
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (selectedDate < today) return true // Block past On Hold dates
    enterEditModeForDate(clickedDate)
    return true
  }

  const handleDateClick = (clickedDate: string) => {
    if (isPastDate(clickedDate)) return

    const normalizedClickedDate = normalizeDate(clickedDate)
    const containingBid = vacationBids.find((b) =>
      b.dates.map(normalizeDate).includes(normalizedClickedDate)
    )

    // "Add date to request" bulk-add flow
    const isBulkAddWithoutBid = editMode && !editingBidId && editOriginalDates.length > 0
    if (isBulkAddWithoutBid) return

    if (handleHistoryTableDateClick(normalizedClickedDate, containingBid, clickedDate)) return

    if (containingBid) {
      handleEditBid(containingBid.id, clickedDate)
      return
    }

    const apiKey = convertMMDDYYYYToYYYYMMDD(clickedDate)
    const tooltipEntry = otherRequestDetailsForTooltip[apiKey]
    if (tooltipEntry?.durationLabel === 'Partial Day') return

    if (handleOnHoldDateClick(clickedDate, apiKey)) return

    resetFreshSelectionState()
    setHoursError('')
    setChoiceRankError('')
    setBannerMessages(null)

    if (editMode && editDate && clickedDate !== editDate && selectedDates.includes(clickedDate)) {
      switchEditTarget(clickedDate)
    }
  }

  const enterEditModeForDate = (dateStr: string) => {
    const normalized = normalizeDate(dateStr)
    const containingBid = vacationBids.find((b) =>
      b.dates.map(normalizeDate).includes(normalized)
    )

    // If the date belongs to a bid in the CURRENT session, enter edit mode for it
    if (containingBid) {
      setEditMode(true)
      setEditDate(normalized)
      setShowHistoryTable(false)
      setSelectedDates(containingBid.dates.map(normalizeDate))
      setHoursPerDay(getDateHours(containingBid, normalized))
      const selectedOption =
        dropdownOptions.find((opt) => opt.value === containingBid.choiceRank) ||
        null
      setSelectedRank(selectedOption)
      setEditingBidId(containingBid.id)
      return
    }

    // The on-hold date is from a PAST API submission (not in current session bids).
    // If currently editing a bid, exit edit mode first (same as original behavior).
    if (editMode) {

      setEditMode(false)
      setEditDate(null)
      setEditingBidId(null)
      setShowHistoryTable(false)
    }
    // Toggle: second click on the same on-hold date deselects it (back to on-hold colour);
    // first click adds it to the in-progress selection.
    if (selectedDates.map(normalizeDate).includes(normalized)) {
      setSelectedDates((prev) =>
        prev.filter((d) => normalizeDate(d) !== normalized)
      )
      // Clear errors when deselecting dates
      setHoursError('')
      setChoiceRankError('')
      setBannerMessages(null)
    } else {
      setSelectedDates((prev) => [...prev, normalized])
      // Clear errors when adding new dates
      setHoursError('')
      setChoiceRankError('')
      setBannerMessages(null)
      if (lastChoiceRank) setSelectedRank(lastChoiceRank)
    }
  }

  const switchEditTarget = (newDate: string) => {
    setEditDate(newDate)
    if (editingBidId) {
      const bid = vacationBids.find((b) => b.id === editingBidId)
      if (bid) {
        setHoursPerDay(getDateHours(bid, newDate))
      }
    }
  }

  useEffect(() => {
    if (!selectedDates || selectedDates.length === 0) {
      setBannerMessages(null)
    }
  }, [selectedDates])

  // Format dates for display — delegates to the module-level normalizeDate utility
  // to avoid duplicating the same MM/DD/YYYY formatting logic.
  const formatDate = normalizeDate

  const sortedDates = [...selectedDates]
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
    .map(formatDate)

  const getOriginalBidHoursForDate = (date: string): number => {
    if (!originalEditingBid) return 0

    if (typeof originalEditingBid.hoursPerDay === 'string') {
      return Number.parseFloat(originalEditingBid.hoursPerDay) || 0
    }

    const normalizedDate = normalizeDate(date)
    return (
      Number.parseFloat(
        originalEditingBid.hoursPerDay[normalizedDate] ||
          originalEditingBid.hoursPerDay[date] ||
          '0'
      ) || 0
    )
  }

  // In "Add date to request" mode, selectedDates intentionally includes
  // committed dates for calendar highlighting. Total hours in the form must
  // only reflect newly added dates that are currently being drafted.
  const datesCountForTotal =
    editMode && !editingBidId && editOriginalDates.length > 0
      ? newlyAddedEditDates.length
      : selectedDates.length

  const totalHours = (() => {
    if (editMode && editingBidId && originalEditingBid) {
      const editedDateNormalized = editDate ? normalizeDate(editDate) : null
      const newlyAddedSet = new Set(newlyAddedEditDates.map(normalizeDate))
      const liveHours = Number.parseFloat(hoursPerDay) || 0

      return selectedDates.reduce((sum, date) => {
        const normalizedDate = normalizeDate(date)
        if (
          (editedDateNormalized && normalizedDate === editedDateNormalized) ||
          newlyAddedSet.has(normalizedDate)
        ) {
          return sum + liveHours
        }

        return sum + getOriginalBidHoursForDate(date)
      }, 0)
    }

    return datesCountForTotal * (Number.parseFloat(hoursPerDay) || 0)
  })()

  const committedDates = useMemo(() => {
    return Array.from(new Set(vacationBids.flatMap((bid) => bid.dates))).sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    )
  }, [vacationBids])

  // Flatten all dates currently in vacationBids for calendar review mode
  const allBidDates = vacationBids.flatMap((b) => b.dates)

  const resetFreshSelectionState = () => {
    setHoursPerDay('0')
    setSelectedRank(null)
    setLastChoiceRank(null)
  }

  const openBlankVacationBidForDate = (date: string) => {
    setSelectedDates([date])
    setEditMode(false)
    setEditDate(null)
    setEditingBidId(null)
    setShowHistoryTable(false)
    resetFreshSelectionState()
  }
  const handleVacationBidEditSelectionChange = (
    newSelected: string[]
  ): boolean => {
    // editDate is only set when editing a specific bid's specific date
    // (pencil-icon edit). The "Add date to request" bulk-add flow also uses
    // editMode with editDate left null, and still needs new selections
    // tracked against editOriginalDates, so editDate is intentionally not
    // required here.
    if (!editMode) {
      return false
    }

    const normalized = newSelected.map(normalizeDate)

    // Normalize both sides before comparing — editOriginalDates is seeded
    // from bid.dates, which isn't guaranteed to already be in normalized
    // form, so a raw string-equality check here could fail to recognise an
    // already-committed date and wrongly treat it as newly added.
    const editOriginalDatesNormalized = new Set(
      editOriginalDates.map(normalizeDate)
    )
    const newDates = normalized.filter(
      (date) => !editOriginalDatesNormalized.has(date)
    )
    setNewlyAddedEditDates(newDates)
  
    // IMPORTANT:
    // selectedDates is only UI state
    // Do not make it the source of truth
    setSelectedDates(() => {
      const updated = Array.from(new Set([...editOriginalDates, ...newDates]))

      return updated
    })

    return true
  }
  const handleVacationBidHistorySelectionChange = (
    newSelected: string[]
  ): boolean => {
    if (!(showHistoryTable && !editMode)) return false

    const existingSet = new Set(allBidDates.map(normalizeDate))
    const extras = newSelected.filter((date) => !existingSet.has(date))
    if (extras.length > 0) {
      setSelectedDates(extras)
      if (lastChoiceRank) setSelectedRank(lastChoiceRank)
    }

    return true
  }

  const handleVacationBidFreshCommittedSelectionChange = (
    newSelected: string[]
  ): boolean => {
    const isCommittedSelection =
      selectedDates.length > 0 &&
      selectedDates.every((date) => committedDates.includes(date))

    if (!isCommittedSelection) return false

    const freshDate = newSelected.find((date) => !committedDates.includes(date))
    if (freshDate) {
      openBlankVacationBidForDate(freshDate)
      return true
    }

    return false
  }

  const handleAddToVacationBid = () => {
    if (selectedDates.length === 0) {
      alert('Please select dates')
      return
    }

    const errorMessages: string[] = []
    let hasHoursError = false
    let hasChoiceRankError = false

    // Validate hoursPerDay
    const hoursValidationError = validateHours(hoursPerDay)
    if (hoursValidationError) {
      hasHoursError = true
      setHoursError(hoursValidationError)
      const bannerMsg =
        hoursValidationError === 'Revise'
          ? ValidationMessages.DURATION
          : hoursValidationError
      errorMessages.push(bannerMsg)
    } else {
      const { messages: entryMessages } = validateVacationBiddingEntries({
        vacation: hoursPerDay,
      })
      if (entryMessages?.includes(ValidationMessages.DURATION)) {
        hasHoursError = true
        setHoursError(ValidationMessages.DURATION)
        errorMessages.push(ValidationMessages.DURATION)
      }
    }

    // Validate choice rank is selected
    if (!selectedRank) {
      hasChoiceRankError = true
      setChoiceRankError('Required')
      errorMessages.push(CHOICE_RANK_BANNER_MESSAGE)
    }

    // Validate 8-day gap between selected dates before adding to the bid
    // Validate 8-day gap across all dates in the request (existing bids + newly selected)
    const allDatesForGapCheck = [
      ...vacationBids.flatMap((bid) => bid.dates),
      ...selectedDates,
    ]
    const dateGapError = validateDateGaps(allDatesForGapCheck)
    if (dateGapError) {
      errorMessages.push(dateGapError)
    }

    // If any errors, show all banner messages and return
    if (errorMessages.length > 0 || hasHoursError || hasChoiceRankError) {
      setBannerMessages(errorMessages)
      scrollToTopAndFocusAlert()
      return
    }

    const choiceErr = validateChoiceRank(selectedRank?.value)
    if (choiceErr) {
      setBannerMessages([choiceErr])
      scrollToTopAndFocusAlert()
      return
    }

    // This handler is only wired to the "Next: Review Request" button, which
    // ActionButtons only renders when editMode is false — so this is always a
    // genuine fresh bid, never the "Add date to request" bulk-add flow (that
    // flow's dates go through handleUpdateBid's isBulkAddWithoutBid branch
    // instead, since its button is "Update").
    const newBid: VacationBidEntry = {
      id: Date.now().toString(),
      dates: selectedDates,
      hoursPerDay,
      totalHours: selectedDates.length * (Number.parseFloat(hoursPerDay) || 0),
      comments: '',
      choiceRank: Object.fromEntries(
        selectedDates.map((date) => [
          normalizeDate(date),
          selectedRank?.value || '',
        ])
      ),
    }

    setVacationBids((prev) => [
      ...applyRankToAllBids(prev, selectedRank?.value || ''),
      newBid,
    ])

    // Clear form and return to history table
    setBannerMessages(null)
    setHoursError('')
    setChoiceRankError('')
    setSelectedDates([])
    setHoursPerDay('0')
    setSelectedRank(null)
    setLastChoiceRank(selectedRank)
    setShowHistoryTable(true)

    // CRITICAL: Reset "Add More Dates" tracking state to prevent future merges
    setEditMode(false)
    setEditingBidId(null)
    setEditOriginalDates([])
    setNewlyAddedEditDates([])
  }

  const handleRemoveBid = (bidId: string, dateStr: string) => {
    const updateBidWithRemovedDate = (
      bid: VacationBidEntry
    ): VacationBidEntry | null => {
      if (bid.id !== bidId) return bid

      const updatedDates = bid.dates.filter(
        (d) => normalizeDate(d) !== normalizeDate(dateStr)
      )
      if (updatedDates.length === 0) return null // Remove bid if no dates remain

      // Handle both string and per-date hoursPerDay
      let updatedHours: string | Record<string, string> = bid.hoursPerDay
      if (typeof bid.hoursPerDay !== 'string') {
        const hoursMap = { ...bid.hoursPerDay }
        delete hoursMap[dateStr]
        updatedHours = hoursMap
      }

      const updatedTotalHours = updatedDates.reduce((sum, d) => {
        const hours =
          typeof updatedHours === 'string'
            ? updatedHours
            : updatedHours[d] || '0'
        return sum + (Number.parseFloat(hours) || 0)
      }, 0)

      return {
        ...bid,
        dates: updatedDates,
        hoursPerDay: updatedHours,
        totalHours: updatedTotalHours,
      }
    }

    const updatedBids = vacationBids
      .map(updateBidWithRemovedDate)
      .filter((bid): bid is VacationBidEntry => bid !== null)

    setVacationBids(updatedBids)

    // If all bids were removed, reset the form and return to calendar
    if (updatedBids.length === 0) {
      resetFormState(false)
      // Removing the last bid cancels the request entirely, so the request-level
      // comment and remembered choice rank must be cleared too. resetFormState is
      // intentionally left untouched here because it is also called mid-flow by
      // handleUpdateBid, where the in-progress comment/last rank must be preserved.
      setComments('')
      setLastChoiceRank(null)
    }
  }

  const handleEditBid = (id: string, dateStr: string) => {
    
    const bidToEdit = vacationBids.find((b) => b.id === id)

    if (!bidToEdit) return
    setNewlyAddedEditDates([])
    const originalDates = bidToEdit.dates.map(normalizeDate)

    setOriginalEditingBid({
      ...bidToEdit,
      dates: [...originalDates],
    })

    setEditOriginalDates(originalDates)

    // IMPORTANT:
    // New dates should be tracked separately
    setSelectedDates(originalDates)
    setEditOriginalDates(originalDates)

    setHoursPerDay(getDateHours(bidToEdit, normalizeDate(dateStr)))
    const existingHoursMap =
      typeof bidToEdit.hoursPerDay === 'string'
        ? Object.fromEntries(
            bidToEdit.dates.map((d) => [
              normalizeDate(d),
              bidToEdit.hoursPerDay as string,
            ])
          )
        : { ...bidToEdit.hoursPerDay }

    setHoursPerDay(existingHoursMap[normalizeDate(dateStr)] || '0')

    const rank =
  typeof bidToEdit.choiceRank === 'string'
    ? bidToEdit.choiceRank
    : bidToEdit.choiceRank[normalizeDate(dateStr)]

setSelectedRank(
  dropdownOptions.find((opt) => opt.value === rank) || null
)

    setEditingBidId(id)
    setEditMode(true)
    setEditDate(normalizeDate(dateStr))
    setShowHistoryTable(false)
  }

  const handleBackFromEdit = () => {
    // Return to history table without saving changes
    setEditMode(false)
    setEditDate(null)
    setEditingBidId(null)
    setOriginalEditingBid(null)
    setShowHistoryTable(true)
    setBannerMessages(null)
    setHoursError('')
    setChoiceRankError('')
  }

  const resetFormState = (showHistoryAfterReset: boolean = true) => {
    setEditMode(false)
    setEditDate(null)
    setEditingBidId(null)
    setOriginalEditingBid(null)
    setSelectedDates([])
    setHoursPerDay('0')
    setSelectedRank(null)
    setShowHistoryTable(showHistoryAfterReset)
    setBannerMessages(null)
    setHoursError('')
    setChoiceRankError('')
  }

  /** Validates hours and choice rank for handleUpdateBid. Returns error messages array. */
  const validateUpdateBidFields = (): string[] => {
    const errorMessages: string[] = []

    const hoursValidationError = validateHours(hoursPerDay)
    if (hoursValidationError) {
      setHoursError(hoursValidationError)
      errorMessages.push(hoursValidationError === 'Revise' ? ValidationMessages.DURATION : hoursValidationError)
    } else {
      const { messages: entryMessages } = validateVacationBiddingEntries({ vacation: hoursPerDay })
      if (entryMessages?.includes(ValidationMessages.DURATION)) {
        setHoursError(ValidationMessages.DURATION)
        errorMessages.push(ValidationMessages.DURATION)
      }
    }

    if (!selectedRank) {
      setChoiceRankError('Required')
      errorMessages.push('Please select a Choice Rank')
    }

    // Validate 8-day gap (only for new bid path, not per-date edits)
    if (!(editingBidId && editDate && originalEditingBid)) {
      const allDatesForGapCheck = [...vacationBids.flatMap((bid) => bid.dates), ...selectedDates]
      const dateGapError = validateDateGaps(allDatesForGapCheck)
      if (dateGapError) errorMessages.push(dateGapError)
    }

    return errorMessages
  }

  /** Updates an existing bid with merged hours/dates. Returns true if handled. */
  const updateExistingBid = (): boolean => {
    if (!editingBidId || !originalEditingBid) return false

    const existingHours: Record<string, string> =
      typeof originalEditingBid.hoursPerDay === 'string'
        ? Object.fromEntries(editOriginalDates.map((d) => [d, originalEditingBid.hoursPerDay as string]))
        : Object.fromEntries(editOriginalDates.map((d) => [d, (originalEditingBid.hoursPerDay as Record<string, string>)[d]]))

    const mergedHours: Record<string, string> = { ...existingHours }
    if (editDate) mergedHours[normalizeDate(editDate)] = hoursPerDay
    newlyAddedEditDates.forEach((d) => { mergedHours[normalizeDate(d)] = hoursPerDay })

    const updatedDates = Array.from(new Set([...editOriginalDates, ...newlyAddedEditDates]))
    const totalHrs = updatedDates.reduce((sum, d) => sum + Number.parseFloat(mergedHours[d] || '0'), 0)
    const rankValue = selectedRank?.value || ''
    const existingRanks: Record<string, string> = Object.fromEntries(updatedDates.map((d) => [normalizeDate(d), rankValue]))

    const updatedBid: VacationBidEntry = {
      ...originalEditingBid,
      dates: updatedDates,
      hoursPerDay: mergedHours,
      totalHours: totalHrs,
      choiceRank: existingRanks,
      comments,
    }

    setVacationBids((prev) => applyRankToAllBids(prev.map((bid) => (bid.id === editingBidId ? updatedBid : bid)), rankValue))
    setEditOriginalDates([])
    resetFormState()
    return true
  }

  const handleUpdateBid = () => {
    const errorMessages = validateUpdateBidFields()
    if (errorMessages.length > 0) {
      setBannerMessages(errorMessages)
      scrollToTopAndFocusAlert()
      return
    }

    if (updateExistingBid()) return
    // For new bids, use uniform hours across all dates.
    // When editingBidId is null we're either in the "Add date to request"
    // bulk-add flow (editOriginalDates seeded with the already-committed
    // dates) or a genuine fresh add — in the bulk-add case, selectedDates
    // also contains the untouched old dates (merged in for display), so the
    // new bid must only carry newlyAddedEditDates to avoid re-submitting the
    // old dates a second time as a duplicate bid.
    const isBulkAddWithoutBid = !editingBidId && editOriginalDates.length > 0
    if (isBulkAddWithoutBid && newlyAddedEditDates.length === 0) {
      // Nothing new was selected — there is nothing to add.
      setEditOriginalDates([])
      setNewlyAddedEditDates([])
      resetFormState()
      return
    }
    // Defense-in-depth: no date already committed to ANY existing bid should
    // ever be treated as "new" here — that would create a second row for a
    // date that already has a row, showing the old date with new details
    // alongside its original old-details row. editOriginalDates/
    // newlyAddedEditDates already do this filtering, but re-checking against
    // the live vacationBids dates (rather than the editOriginalDates snapshot
    // taken when "Add date to request" was clicked) closes the gap if that
    // snapshot and the current bids ever drift apart.
    const committedDateSet = new Set(
      vacationBids.flatMap((bid) => bid.dates.map(normalizeDate))
    )
    const bidDates = isBulkAddWithoutBid
      ? newlyAddedEditDates.filter(
          (date) => !committedDateSet.has(normalizeDate(date))
        )
      : selectedDates
    if (isBulkAddWithoutBid && bidDates.length === 0) {
      // Every "newly added" date turned out to already belong to an
      // existing bid — nothing to add.
      setEditOriginalDates([])
      setNewlyAddedEditDates([])
      resetFormState()
      return
    }
    const rankValue = selectedRank?.value || ''

    // "Add date to request" bulk-add flow: if the newly added dates share the
    // same choice rank as an existing bid, merge them into that bid instead
    // of creating a second one — otherwise submitAllBids (one API call per
    // bid) would submit them as two separate time-off requests instead of
    // extending the single request the user is building.
    const mergeTargetBid = isBulkAddWithoutBid
      ? vacationBids.find((bid) => {
          const bidRank =
            typeof bid.choiceRank === 'string'
              ? bid.choiceRank
              : Object.values(bid.choiceRank)[0]
          return bidRank === rankValue
        })
      : null

    if (mergeTargetBid) {
      const existingHours: Record<string, string> =
        typeof mergeTargetBid.hoursPerDay === 'string'
          ? Object.fromEntries(
              mergeTargetBid.dates.map((d) => [
                normalizeDate(d),
                mergeTargetBid.hoursPerDay as string,
              ])
            )
          : { ...mergeTargetBid.hoursPerDay }

      const existingRanks: Record<string, string> =
        typeof mergeTargetBid.choiceRank === 'string'
          ? Object.fromEntries(
              mergeTargetBid.dates.map((d) => [
                normalizeDate(d),
                mergeTargetBid.choiceRank as string,
              ])
            )
          : { ...mergeTargetBid.choiceRank }

      bidDates.forEach((date) => {
        existingHours[normalizeDate(date)] = hoursPerDay
        existingRanks[normalizeDate(date)] = rankValue
      })

      const mergedDates = Array.from(
        new Set([
          ...mergeTargetBid.dates.map(normalizeDate),
          ...bidDates.map(normalizeDate),
        ])
      )
      const mergedTotalHours = mergedDates.reduce(
        (sum, d) => sum + (Number.parseFloat(existingHours[d] || '0') || 0),
        0
      )

      const mergedBid: VacationBidEntry = {
        ...mergeTargetBid,
        dates: mergedDates,
        hoursPerDay: existingHours,
        totalHours: mergedTotalHours,
        choiceRank: existingRanks,
        comments,
      }

      setVacationBids((prev) =>
        applyRankToAllBids(
          prev.map((bid) => (bid.id === mergeTargetBid.id ? mergedBid : bid)),
          rankValue
        )
      )
    } else {
      const idToUse = editingBidId || Date.now().toString()
      const updatedBid: VacationBidEntry = {
        id: idToUse,
        dates: bidDates,
        hoursPerDay,
        totalHours: bidDates.length * (Number.parseFloat(hoursPerDay) || 0),
        comments,
        choiceRank: Object.fromEntries(
          bidDates.map((date) => [normalizeDate(date), rankValue])
        ),
      }
      setVacationBids((prev) => {
        const next = editingBidId
          ? prev.map((bid) => (bid.id === editingBidId ? updatedBid : bid))
          : [...prev, updatedBid]
        return applyRankToAllBids(next, rankValue)
      })
    }

    setEditOriginalDates([])
    setNewlyAddedEditDates([])
    setOriginalEditingBid(null)
    resetFormState()
  }


  const dropdownOptions: DropdownOption[] = [
    { id: 1, label: '1', value: '1' },
    { id: 2, label: '2', value: '2' },
    { id: 3, label: '3', value: '3' },
    { id: 4, label: '4', value: '4' },
    { id: 5, label: '5', value: '5' },
    { id: 6, label: 'Not applicable', value: 'Not applicable' },
  ]

  /**
   * Removes only the banner messages matching the predicate, preserving any
   * unrelated errors. Collapses the banner back to `null` once empty so the
   * banner container is hidden when no messages remain.
   *
   * @param predicate - Returns true for messages that should be removed.
   */
  const removeBannerMessages = (predicate: (msg: string) => boolean) => {
    setBannerMessages((prev) => withoutBannerMessages(prev, predicate))
  }

  // clear banner when user selects a rank — only the choice/rank message is
  // removed so an unresolved hours error stays visible.
  const handleDropdownSelectWithClear = (option: DropdownOption) => {
    setSelectedRank(option)
    setLastChoiceRank(option)
    removeBannerMessages(isChoiceRankBannerMessage)
    if (choiceRankError) setChoiceRankError('')
  }

  return (
    <div className="page-container min-h-[70vh] lg:mx-40! desktop:mx-[120px]! tablet:mx-5! mobile:mx-4!">
      <BreadCrumbs
        previousPage="TORT"
        currentPage="Create a Vacation Bid Request"
        onPreviousClick={handleBreadcrumbPrevious}
      />
      <h1 className="h1 desktop:mt-[60px]! tablet:mt-[50px]! mobile:mt-8!">
        Create a Vacation Bid Request
      </h1>
      {isAdminMode ? (
        <div className="mb-4">
          <ManualTimeOffBanner />
        </div>
      ) : (
        <>
          <p className="p3 -book mb-0!">You are logged in as:</p>
          <h3 className="h3 mb-0! mt-0!">
            {userInfo} {userNuid}
          </h3>
          <p className="p3 -book mt-0! desktop:mb-2!">
            Primary Approver: {primaryApproverInfo}
          </p>
        </>
      )}
      {renderBannerMessages()}
      <div className="mt-8 flex flex-col lg:flex-row gap-4 lg:gap-16 items-start">
        {
          <div className={showHistoryTable ? 'hidden' : 'w-full lg:w-auto'}>
              <div className="flex items-center">
                <h2 className="h2 text-lg mb-3">Select Dates</h2>
                {/* Colour legend tooltip */}
                <div className="ml-2">
                  <ColorLegendTooltip />
                </div>
              </div>
            <Datepicker
              name="vacationDates"
              label="Select Dates"
              value={showHistoryTable ? allBidDates : selectedDates}
              onChange={handleDateChange}
              onDateClick={handleDateClick}
              onMonthYearChange={handleMonthYearChange}
              required
              readOnly={false}
              openBehaviour="calendarView"
              isEditMode={editMode && !!editDate}
              editDate={editDate}
              editModeSelectedDates={selectedDates}
              canEnterEditMode={showHistoryTable}
              highlightVacationBidOnHold
              committedDates={committedDates}
              otherSelectedDatesToShow={otherRequestDates}
              otherRequestDetails={otherRequestDetailsForTooltip}
              calendarWidth={372}
              calendarHeight={399}
            />
          </div>
        }
        <div className={showHistoryTable ? 'w-full' : 'w-full lg:flex-1'}>
          {!showHistoryTable && renderFormSection()}
          {showHistoryTable && renderHistorySection()}
        </div>
      </div>
      <TimeOffBalance />
      <EmployeeInfo />
    </div>
  )
  function renderBannerMessages() {
    if (!bannerMessages || bannerMessages.length === 0) return null
    return (
      <div className="space-y-3 mb-8! mt-8!">
        {bannerMessages.map((msg) => {
          if (msg === ValidationMessages.REQUEST_DATES_EXCEED_8_DAY_GAP) {
            return (
              <ValidationBanner
                key={msg}
                variant="error"
                heading={<p className="p1 -medium">{msg}</p>}
                message={
                  <p className="p3 -book mt-2">
                    If these dates represent more than one time off request,
                    please submit them as separate requests.
                  </p>
                }
                compact={false}
              />
            )
          }
          return (
            <ValidationBanner
              key={msg}
              variant="error"
              heading={<p className="p1 -medium">{msg}</p>}
              compact
            />
          )
        })}
      </div>
    )
  }

  function renderFormSection() {
    return (
      <FormContent
        editMode={editMode}
        selectedDates={selectedDates}
        sortedDates={sortedDates}
        hoursPerDay={hoursPerDay}
        totalHours={totalHours}
        selectedRank={selectedRank}
        dropdownOptions={dropdownOptions}
        onHoursChange={setHoursPerDay}
        onSelectRank={handleDropdownSelectWithClear}
        onAdd={handleAddToVacationBid}
        onBack={handleBackFromEdit}
        onCancel={handleCancelBid}
        onUpdate={handleUpdateBid}
        hoursError={hoursError}
        onHoursErrorClear={() => {
          setHoursError('')
          removeBannerMessages(isHoursBannerMessage)
        }}
        choiceRankError={choiceRankError}
        onChoiceRankErrorClear={() => {
          setChoiceRankError('')
          removeBannerMessages(isChoiceRankBannerMessage)
        }}
        newlyAddedEditDates={newlyAddedEditDates}
        editOriginalDates={editOriginalDates}
        editingBidId={editingBidId}
      />
    )
  }

  function renderHistorySection() {
    return (
      <HistoryTableSection
        vacationBids={vacationBids}
        comments={comments}
        onEditBid={handleEditBid}
        onRemoveBid={handleRemoveBid}
        onCommentsChange={setComments}
        onCancel={handleCancelBid}
        onSaveDraft={handleSaveDraftAllBids}
        onSubmit={handleSubmitAllBids}
        isSubmitting={isSubmitting}
        activeSubmitAction={activeSubmitAction}
        adminReceivedDate={adminReceivedDate}
        setAdminReceivedDate={setAdminReceivedDate}
        onAdminDateError={setAdminReceivedDateError}
        adminReceivedDateError={adminReceivedDateError}
        isAdminMode={isAdminMode}
        onAddMoreDates={handleAddMoreDates}
        otherReqData={otherReqData}
      />
    )
  }
}

export default CreateVacationBidRequest

// ============================================================================
// EMBEDDED FORM — for DraftView right-panel use (no page chrome)
// ============================================================================

/** Normalised row shape emitted to the parent via onReviewReady */
export interface VacationBidReviewRow {
  id: string
  requestType: string
  date: string
  day: string
  times: string
  requestedHours: string
  /** Matches legacy's "Day in Other Request(s)" checkbox — true when another existing request already has a Vacation Bid allocation on this date. */
  hoursInOtherRequests: boolean
  choiceRank?: string
}

export interface EmbeddedVacationBidFormProps {
  /** Pre-selected dates (MM/DD/YYYY). */
  selectedDates: string[]
  existingDraftDates?: string[]
  /** Called when the user cancels — parent should unmount the form. */
  onCancelRequest?: () => void
  /**
   * Called whenever the accumulated bid table changes (add, edit, remove).
   * Parent is responsible for displaying the resulting rows in its own table.
   */
  onReviewReady?: (rows: VacationBidReviewRow[]) => void
  /**
   * When true the "Dates added to Request" review table is hidden.
   * The component instead shows a comment section + Cancel / Save Draft / Submit
   * buttons so the parent page can handle the submission phase.
   * Used by DraftView where entries flow into the parent "Current Request" table.
   */
  hideReviewTable?: boolean
  /** Called with the current comment string and resolved rank when "Save as Draft" is clicked (hideReviewTable mode only). */
  onSaveDraft?: (comments: string, rank?: number) => void
  /** Called with the current comment string and resolved rank when "Submit Vacation Bid" is clicked (hideReviewTable mode only). */
  onSubmitRequest?: (comments: string, rank?: number) => void
  /**
   * Previously saved vacation-bid hours keyed by date (MM/DD/YYYY) so the edit
   * form pre-fills the user's existing value instead of 0. Used by DraftView.
   */
  initialHoursByDate?: Record<string, string>
  /** Previously saved choice/rank used to pre-fill the rank dropdown (DraftView edit flow). */
  initialRank?: number | null
  /**
   * Previously saved comments pre-filled in the comment textarea when the form
   * opens in edit mode (DraftView). The user can edit or clear them before saving.
   */
  initialComments?: string
  /**
   * When true, the Save as Draft and Submit buttons are disabled and show a
   * submitting state while the parent API call is in flight.
   */
  isSubmitting?: boolean
  /**
   * The draft request currently being edited — excluded from its own
   * "Day in Other Requests" overlap check, since it would otherwise appear
   * in otherReqData (already persisted) and match against itself.
   */
  currentRequestId?: string
}

type SubmitAction = 'save' | 'submit' | null

/** Dropdown options shared between the full page and embedded form. */
const RANK_DROPDOWN_OPTIONS: DropdownOption[] = [
  { id: 1, label: '1', value: '1' },
  { id: 2, label: '2', value: '2' },
  { id: 3, label: '3', value: '3' },
  { id: 4, label: '4', value: '4' },
  { id: 5, label: '5', value: '5' },
  { id: 6, label: 'Not applicable', value: 'Not applicable' },
]

/**
 * Resolve the previously-saved hours for the single active date (DraftView edit
 * flow). Returns '0' when no saved-hours map is supplied or more than one date
 * is active, so the create flow keeps its empty default.
 *
 * @param initialHoursByDate - Saved hours keyed by date (MM/DD/YYYY).
 * @param selectedDates - Currently active dates (MM/DD/YYYY).
 * @returns The seeded hours string, or '0' when nothing is saved.
 */
const resolveSeededVacHours = (
  initialHoursByDate: Record<string, string> | undefined,
  selectedDates: string[]
): string => {
  if (!initialHoursByDate || selectedDates.length !== 1) return '0'
  return initialHoursByDate[selectedDates[0]] ?? '0'
}

/**
 * Resolve the rank dropdown option that matches a previously-saved numeric rank
 * (DraftView edit flow), or null when there is no match.
 *
 * @param initialRank - The saved choice/rank value (torRank).
 * @returns The matching dropdown option, or null.
 */
const resolveSeededVacRank = (
  initialRank: number | null | undefined
): DropdownOption | null =>
  RANK_DROPDOWN_OPTIONS.find((o) => o.value === String(initialRank)) ?? null

/**
 * Format a date string to MM/DD/YYYY for display in the embedded form.
 * Delegates to the module-level normalizeDate utility to avoid
 * duplicating the same logic as formatDate inside CreateVacationBidRequest.
 */
const formatEmbeddedDate = normalizeDate

/** Derive flat review rows from all accumulated bids. */
const buildReviewRows = (
  bids: VacationBidEntry[],
  otherReqData: Record<string, any[]> | null | undefined,
  currentRequestId?: string,
): VacationBidReviewRow[] => {
  const currentRequestDetailId = (() => {
    if (!currentRequestId) return undefined
    const parsed = Number(currentRequestId)
    return Number.isNaN(parsed) ? undefined : parsed
  })()

  return bids.flatMap((bid, bidIdx) =>
    bid.dates.map((dateStr, dateIdx) => {
      const dateObj = new Date(dateStr)
      return {
        id: `vb-row-${bidIdx}-${dateIdx}`,
        requestType: 'Vacation Bidding',
        date: dateObj.toLocaleDateString('en-US', {
          month: '2-digit',
          day: '2-digit',
          year: 'numeric',
        }),
        day: dateObj.toLocaleDateString('en-US', { weekday: 'short' }),
        times: 'Full Day',
        requestedHours: (
          Number.parseFloat(getDateHours(bid, dateStr)) || 0
        ).toFixed(2),
        hoursInOtherRequests: isVacBidDayInOtherRequestsChecked(
          otherReqData,
          dateStr,
          currentRequestDetailId
        ),
        choiceRank: getDateRank(bid, dateStr),
          _sortDate: dateObj.getTime(),
        }
      })
    )
    // Bids are appended in creation order, so a date added later via "Add
    // date to request" can be earlier than dates already in the list —
    // sort the flattened rows ascending by date so the review table always
    // reads in chronological order.
    .sort((a, b) => a._sortDate - b._sortDate)
    .map(({ _sortDate, ...row }) => row)
  }
/**
 * Embeddable vacation bid form intended for the DraftView right panel.
 * Mirrors the add-entry → review-table flow of the full CreateVacationBidRequest
 * page but without page chrome (no breadcrumbs, title, or datepicker).
 *
 * After "Next: Review Request" the component either shows:
 * - The accumulated bid table + Cancel button (default, used by the full-page flow)
 * - A comment section + Cancel / Save as Draft / Submit buttons when
 *   `hideReviewTable` is true (used by DraftView where rows go to the parent table)
 *
 * Clicking a new date on the parent calendar triggers a `selectedDates` prop
 * change which automatically returns the form to entry mode for that new date.
 */
/**
 * Renders the embedded vacation-bid form used inside DraftView.
 * @param props - Embedded form props including selected dates and callbacks.
 * @returns The embedded vacation-bid form.
 */
export const EmbeddedVacationBidForm: React.FC<
  EmbeddedVacationBidFormProps
> = ({
  selectedDates,
  onCancelRequest,
  onReviewReady,
  hideReviewTable = false,
  onSaveDraft,
  onSubmitRequest,
  initialHoursByDate,
  initialRank = null,
  initialComments = '',
  isSubmitting = false,
  currentRequestId,
  existingDraftDates=[],
}) => {
  const [hoursPerDay, setHoursPerDay] = useState<string>(() =>
    resolveSeededVacHours(initialHoursByDate, selectedDates)
  )
  const [selectedRank, setSelectedRank] = useState<DropdownOption | null>(() =>
    resolveSeededVacRank(initialRank)
  )
  const [hoursError, setHoursError] = useState('')
  const [choiceRankError, setChoiceRankError] = useState('')
  const [bannerMessages, setBannerMessages] = useState<string[] | null>(null)
  const [showHistoryTable, setShowHistoryTable] = useState(false)
  const [vacationBids, setVacationBids] = useState<VacationBidEntry[]>([])
  const [embeddedEditMode, setEmbeddedEditMode] = useState(false)
  const [editingBidId, setEditingBidId] = useState<string | null>(null)
  /** Comment text — only used when hideReviewTable=true (DraftView integration). */
  const [embeddedComments, setEmbeddedComments] = useState(
    initialComments || ''
  )
  /** Confirmation modal shown by every Cancel Request button, matching the
   * normal (non-Vacation-Bid) draft request flow instead of cancelling
   * immediately with no confirmation. */
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [activeSubmitAction, setActiveSubmitAction] =
    useState<SubmitAction>(null)

  const dispatch = useDispatch<AppDispatch>()
  const otherReqData = useSelector(
    (state: RootState) => state.otherReqDetails.data
  )

  // Fetch other-request details for every month the currently-selected dates
  // and already-added bids span, so the "Day in Other Requests" checkbox
  // reflects real overlaps rather than always showing unchecked.
  useEffect(() => {
    const allDates = [
      ...selectedDates,
      ...vacationBids.flatMap((bid) => bid.dates),
    ]
    const monthsYears = new Set(
      allDates.map((date) => {
        const parsed = new Date(date)
        return `${parsed.getMonth()}-${parsed.getFullYear()}`
      })
    )
    monthsYears.forEach((key) => {
      const [month, year] = key.split('-').map(Number)
      dispatch(getOtherReqDetails({ month, year }))
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDates.join(','), vacationBids.flatMap((bid) => bid.dates).join(',')])

  /** Keys of the last dates we processed — used to detect new parent-driven dates. */
  const prevDatesKeyRef = React.useRef(selectedDates.join('|'))

  // When the parent passes a NEW set of dates (user clicked a different calendar
  // date), exit review mode and re-seed the form for the new entry. In the
  // DraftView edit flow the seed comes from any previously-saved hours/rank for
  // that date so existing values are retained instead of reset to 0.
  React.useEffect(() => {
    const currentKey = selectedDates.join('|')
    if (currentKey !== prevDatesKeyRef.current) {
      prevDatesKeyRef.current = currentKey
      setShowHistoryTable(false)
      setEmbeddedEditMode(false)
      setEditingBidId(null)
      setHoursPerDay(resolveSeededVacHours(initialHoursByDate, selectedDates))
      setSelectedRank(resolveSeededVacRank(initialRank))
      setBannerMessages(null)
      setHoursError('')
      setChoiceRankError('')
    }
  }, [selectedDates, initialHoursByDate, initialRank])

  // Clear per-button loading label once parent submission cycle completes.
  useEffect(() => {
    if (!isSubmitting) {
      setActiveSubmitAction(null)
    }
  }, [isSubmitting])

  /** Keys of the last `existingDraftDates` set we processed — used to detect
   * dates removed by the parent (DraftView's "Current Request" table). */
  const prevExistingDraftDatesKeyRef = React.useRef(
    existingDraftDates.join('|')
  )

  // `vacationBids` accumulates entries added THIS session via "Next: Review
  // Request" (see handleAdd/handleUpdate below) so they can be forwarded to
  // the parent through `onReviewReady`. In DraftView, the parent's own
  // "Current Request" table (`localTableRows`) is the real source of truth —
  // its remove icon deletes a date directly from that table without this
  // component knowing. Without this effect, a date removed that way stays
  // in `vacationBids` and gets silently re-sent (and re-added) the next time
  // the user commits any other entry via "Next: Review Request"/"Update",
  // since that always re-reports the FULL accumulated list — producing a
  // duplicate row for the date the user had just removed.
  React.useEffect(() => {
    const currentKey = existingDraftDates.join('|')
    if (currentKey === prevExistingDraftDatesKeyRef.current) return
    prevExistingDraftDatesKeyRef.current = currentKey

    const stillValid = new Set([...existingDraftDates, ...selectedDates])

    /** Filters a single bid's dates to only those still valid. Returns null if bid should be removed. */
    const filterBidDates = (bid: VacationBidEntry): VacationBidEntry | null => {
      const keptDates = bid.dates.filter((d) => stillValid.has(d))
      if (keptDates.length === bid.dates.length) return bid
      if (keptDates.length === 0) return null
      const hours = Number.parseFloat(typeof bid.hoursPerDay === 'string' ? bid.hoursPerDay : '8') || 0
      return { ...bid, dates: keptDates, totalHours: keptDates.length * hours }
    }

    setVacationBids((prev) => {
      const next = prev.map(filterBidDates).filter((b): b is VacationBidEntry => b !== null)
      const changed = next.length !== prev.length || next.some((b, i) => b !== prev[i])
      return changed ? next : prev
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingDraftDates])

  const sortedDates = [...selectedDates]
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())
    .map(formatEmbeddedDate)

  const totalHours =
    selectedDates.length * (Number.parseFloat(hoursPerDay) || 0)

  const resetFormFields = () => {
    setHoursPerDay('0')
    setSelectedRank(null)
    setBannerMessages(null)
    setHoursError('')
    setChoiceRankError('')
  }

  /** Resolves the numeric rank to submit — reads from the first accumulated bid's rank (Record or string), falling back to the current dropdown selection. */
  const resolveSubmitRank = (): number | undefined => {
    const bid = vacationBids[0]
    let rankStr: string | undefined
    if (bid) {
      if (typeof bid.choiceRank === 'string') {
        rankStr = bid.choiceRank
      } else {
        rankStr = Object.values(bid.choiceRank)[0]
      }
    } else {
      rankStr = selectedRank?.value
    }
    return rankStr && rankStr !== 'Not applicable'
      ? Number(rankStr) || undefined
      : undefined
  }

  /**
   * Runs the same hours / choice-rank / 8-day-gap validation as
   * "Next: Review Request" (handleAdd). Sets the field-level error states as
   * a side effect and returns the banner messages to show — empty when the
   * current entry is valid.
   */
  const validateEntryFields = (): string[] => {
    const errorMessages: string[] = []

    const hoursValidationError = validateHours(hoursPerDay)
    if (hoursValidationError) {
      setHoursError(hoursValidationError)
      errorMessages.push(
        hoursValidationError === 'Revise'
          ? ValidationMessages.DURATION
          : hoursValidationError
      )
    }

    if (!selectedRank) {
      setChoiceRankError('Required')
      errorMessages.push(CHOICE_RANK_BANNER_MESSAGE)
    }

    // Validate 8-day gap across all dates in the request (existing bids + newly selected)
    const allDatesForGapCheck = [
      ...existingDraftDates.flat(),
      ...selectedDates,
    ]
    const dateGapError = validateDateGaps(allDatesForGapCheck)
    if (dateGapError) {
      errorMessages.push(dateGapError)
    }

    return errorMessages
  }

  const handleCancelModalSaveDraft = () => {
    // Mirror main Save as Draft behavior exactly from this embedded form.
    handleSaveDraftClick()
  }

  const handleCancelModalConfirm = () => {
    onCancelRequest?.()
  }

  const handleSaveDraftClick = () => {
    const allDatesForGapCheck = Array.from(
      new Set([
        ...existingDraftDates.map(normalizeDate),
        ...vacationBids.flatMap((bid) => bid.dates.map(normalizeDate)),
        ...selectedDates.map(normalizeDate),
      ])
    )
    const dateGapError = validateDateGaps(allDatesForGapCheck)
    if (dateGapError) {
      setBannerMessages([dateGapError])
      scrollToTopAndFocusAlert()
      return
    }

    setActiveSubmitAction('save')
    onSaveDraft?.(embeddedComments, resolveSubmitRank())
  }

  const handleSubmitClick = () => {
    const allDatesForGapCheck = Array.from(
      new Set([
        ...existingDraftDates.map(normalizeDate),
        ...vacationBids.flatMap((bid) => bid.dates.map(normalizeDate)),
        ...selectedDates.map(normalizeDate),
      ])
    )
    const dateGapError = validateDateGaps(allDatesForGapCheck)
    if (dateGapError) {
      setBannerMessages([dateGapError])
      scrollToTopAndFocusAlert()
      return
    }

    setActiveSubmitAction('submit')
    onSubmitRequest?.(embeddedComments, resolveSubmitRank())
  }

  /** Shared confirmation modal rendered by every Cancel Request button below —
   * matches the normal (non-Vacation-Bid) draft request flow instead of
   * cancelling immediately with no confirmation and no way to save first. */
  const cancelConfirmationModal = (
    <Modal
      isOpen={showCancelModal}
      title="Cancel Request"
      clearBtn="No, Continue"
      submitBtn="Cancel Request"
      backBtn="Back"
      saveBtn="Save as Draft"
      iconSrc="/assets/images/alertSolid.svg"
      iconAlt="alert icon"
      disableBackdropClick={true}
      onSecondary={() => setShowCancelModal(false)}
      onSaveDraft={handleCancelModalSaveDraft}
      onPrimary={handleCancelModalConfirm}
    >
      <div className="p3 -book">
        You may save your changes as a draft or cancel this request without
        saving.
      </div>
    </Modal>
  )

  const handleAdd = () => {
    const errorMessages = validateEntryFields()

    if (errorMessages.length > 0) {
      setBannerMessages(errorMessages)
      scrollToTopAndFocusAlert()
      return
    }

    const choiceErr = validateChoiceRank(selectedRank?.value)
    if (choiceErr) {
      setBannerMessages([choiceErr])
      scrollToTopAndFocusAlert()
      return
    }

    const newBid: VacationBidEntry = {
      id: Date.now().toString(),
      dates: [...selectedDates],
      hoursPerDay,
      totalHours,
      comments: '',
      choiceRank: Object.fromEntries(
  selectedDates.map(date => [
    normalizeDate(date),
    selectedRank?.value || ''
  ])
),
    }

    const rankValue = selectedRank?.value || ''
    const newBids = [...applyRankToAllBids(vacationBids, rankValue), newBid]
    setVacationBids(newBids)
    setShowHistoryTable(true)
    resetFormFields()
    onReviewReady?.(buildReviewRows(newBids, otherReqData, currentRequestId))
  }

  const handleUpdateBid = () => {
    
    const errorMessages: string[] = []
    let hasErrors = false

    const hoursValidationError = validateHours(hoursPerDay)
    if (hoursValidationError) {
      
      hasErrors = true
      setHoursError(hoursValidationError)
      errorMessages.push(
        hoursValidationError === 'Revise'
          ? ValidationMessages.DURATION
          : hoursValidationError
      )
    }

    if (!selectedRank) {
      hasErrors = true
      setChoiceRankError('Required')
      errorMessages.push(CHOICE_RANK_BANNER_MESSAGE)
    }

    if (hasErrors) {
      setBannerMessages(errorMessages)
      scrollToTopAndFocusAlert()
      return
    }

    const updatedBids = vacationBids.map((bid) => {
      
      if (bid.id !== editingBidId) return bid
      const hours = Number.parseFloat(hoursPerDay) || 0
      const existingRanks =
        typeof bid.choiceRank === 'string'
          ? Object.fromEntries(
              bid.dates.map((date) => [date, bid.choiceRank as string])
            )
          : { ...bid.choiceRank }

      // Update only the currently edited date
      selectedDates.forEach((date) => {
        existingRanks[date] = selectedRank?.value || ''
      })

      return {
        ...bid,
        hoursPerDay,
        totalHours: bid.dates.length * hours,
        choiceRank: existingRanks,
      }
    })

    const rankValue = selectedRank?.value || ''
    const normalizedBids = applyRankToAllBids(updatedBids, rankValue)
    setVacationBids(normalizedBids)
    setEmbeddedEditMode(false)
    setEditingBidId(null)
    setShowHistoryTable(true)
    resetFormFields()
    onReviewReady?.(
      buildReviewRows(normalizedBids, otherReqData, currentRequestId)
    )
  }

  const handleEditBid = (bidId: string) => {
    const bid = vacationBids.find((b) => b.id === bidId)
    if (!bid) return
    setEmbeddedEditMode(true)
    setEditingBidId(bidId)
    setShowHistoryTable(false)
    setHoursPerDay(typeof bid.hoursPerDay === 'string' ? bid.hoursPerDay : '8')
    const bidRank = typeof bid.choiceRank === 'string'
      ? bid.choiceRank
      : Object.values(bid.choiceRank)[0] ?? ''
    const rankOpt = RANK_DROPDOWN_OPTIONS.find((o) => o.value === bidRank) ?? null
    setSelectedRank(rankOpt)
    setBannerMessages(null)
    setHoursError('')
    setChoiceRankError('')
  }

  const handleRemoveBid = (bidId: string, dateStr: string) => {
    const updated = vacationBids
      .map((bid) => {
        if (bid.id !== bidId) return bid
        const newDates = bid.dates.filter((d) => {
          const fmt = new Date(d).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          })
          return fmt !== dateStr && d !== dateStr
        })
        if (newDates.length === 0) return null
        const hours =
          Number.parseFloat(
            typeof bid.hoursPerDay === 'string' ? bid.hoursPerDay : '8'
          ) || 0
        return { ...bid, dates: newDates, totalHours: newDates.length * hours }
      })
      .filter((b): b is VacationBidEntry => b !== null)

    setVacationBids(updated)

    if (updated.length === 0) {
      // No more bids — close the form panel
      setShowHistoryTable(false)
      onCancelRequest?.()
      return
    }

    onReviewReady?.(buildReviewRows(updated, otherReqData, currentRequestId))
  }

  // Comment section + Cancel / Save as Draft / Submit buttons, used by
  // DraftView (hideReviewTable=true). In that mode the entries are already
  // visible in the "Current Request" table above, so this is shown both
  // right when we land on an existing draft (no date selected yet) and
  // after a bid entry has been added/edited — skipping the duplicate table
  // either way and going straight to the comment section + action buttons.
  const renderCommentsAndActions = () => (
    <div>
      {bannerMessages && bannerMessages.length > 0 && (
        <div className="space-y-3 mb-4">
          {bannerMessages.map((msg) => (
            <ValidationBanner
              key={msg}
              variant="error"
              heading={<p className="p1 -medium">{msg}</p>}
              compact
            />
          ))}
        </div>
      )}

      <div className="form-group margin-top-2u mt-4">
        <label
          id="embedded-comments-label"
          className="p4 -medium"
          htmlFor="embedded-comments-textarea"
        >
          Comments
        </label>
        <div className="textarea-wrapper w-full relative">
          <textarea
            autoComplete="on"
            id="embedded-comments-textarea"
            name="embedded-comments"
            aria-labelledby="embedded-comments-label embedded-comments-count"
            className="text-area_word-count text-area_global -book text-base! text-[#0D1C3D]! w-full!"
            data-show-lines="5"
            rows={5}
            maxLength={1000}
            onChange={(e) => setEmbeddedComments(e.target.value)}
            value={embeddedComments}
            placeholder="Add any additional comments about your time off request"
          />
          <div className="character-count">
            <span
              id="embedded-comments-count"
              aria-live="polite"
              aria-atomic="true"
              className="count-area text-input-helper"
              data-description=" of 1000 characters left"
              data-full-text="No more characters available, 1000 of 1000 characters left"
            >
              {1000 - embeddedComments.length} of 1000 characters left
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col-reverse desktop:flex-row gap-3 mt-6! pb-4">
        <button
          type="button"
          className="button -secondary block w-full! desktop:w-auto!"
          onClick={() => setShowCancelModal(true)}
        >
          Cancel
        </button>
        <button
          type="button"
          className="button -secondary block w-full! desktop:w-auto!"
          onClick={handleSaveDraftClick}
          disabled={isSubmitting}
        >
          {isSubmitting && activeSubmitAction === 'save'
            ? 'Saving...'
            : 'Save as Draft'}
        </button>
        <button
          type="button"
          className="button -primary -light block w-full! desktop:w-auto!"
          onClick={handleSubmitClick}
          disabled={isSubmitting}
        >
          {isSubmitting && activeSubmitAction === 'submit'
            ? 'Submitting...'
            : 'Submit Vacation Bid'}
        </button>
      </div>

      {cancelConfirmationModal}
    </div>
  )

  // Empty state — no dates passed yet. In DraftView (hideReviewTable=true)
  // we still want the comment section + action buttons visible as soon as
  // the draft loads, rather than waiting for a date to be selected.
  if (selectedDates.length === 0) {
    if (hideReviewTable) {
      return renderCommentsAndActions()
    }
    return (
      <p className="p3 -medium pt-2 tablet:pt-4 mobile:pt-2">
        Select a date on the calendar to add time to your request
      </p>
    )
  }

  // Review / history table mode
  if (showHistoryTable) {
    // In DraftView (hideReviewTable=true) the entries are already visible in
    // "Current Request" table above. Skip the duplicate table and go straight
    // to the comment section + action buttons.
    if (hideReviewTable) {
      return renderCommentsAndActions()
    }

    // Default (full-page): show the review table + cancel button
    return (
      <div>
        <h2 className="h2 m-0 mb-4 text-lg md:text-xl">
          Dates added to Request
        </h2>
        <p className="p3 -book">
          To add a date, select the Add date to request button. To edit or remove a date, you may use the table below.
        </p>
        <div className="overflow-x-auto mt-4">
          <VacationBidTableDisplay
            vacationBids={vacationBids}
            onEdit={(bidId) => handleEditBid(bidId)}
            onRemove={handleRemoveBid}
            otherReqData={otherReqData}
            currentRequestId={currentRequestId}
          />
        </div>
        <p className="p3 -book mt-4">
          Click a date on the calendar to add more dates to your request.
        </p>
        <div className="flex flex-col-reverse md:flex-row gap-3 mt-6 pb-4">
          <button
            type="button"
            className="button -light block w-full md:w-auto"
            onClick={() => setShowCancelModal(true)}
          >
            Cancel Request
          </button>
        </div>

        {cancelConfirmationModal}
      </div>
    )
  }

  // Edit an existing bid entry
  if (embeddedEditMode) {
    return (
      <div>
        {bannerMessages && bannerMessages.length > 0 && (
          <div className="space-y-3 mb-4">
            {bannerMessages.map((msg) => (
              <ValidationBanner
                key={msg}
                variant="error"
                heading={<p className="p1 -medium">{msg}</p>}
                compact
              />
            ))}
          </div>
        )}
        <EditModeHeader selectedDates={sortedDates} />
        <DurationInput
          hoursPerDay={hoursPerDay}
          onHoursChange={setHoursPerDay}
          error={hoursError}
          onErrorClear={() => {
            setHoursError('')
            setBannerMessages((prev) =>
              withoutBannerMessages(prev, isHoursBannerMessage)
            )
          }}
        />
        <ChoiceRankSelector
          selectedRank={selectedRank}
          onSelectRank={(opt) => {
            setSelectedRank(opt)
            setChoiceRankError('')
            setBannerMessages((prev) =>
              withoutBannerMessages(prev, isChoiceRankBannerMessage)
            )
          }}
          dropdownOptions={RANK_DROPDOWN_OPTIONS}
          error={choiceRankError}
          onErrorClear={() => {
            setChoiceRankError('')
            setBannerMessages((prev) =>
              withoutBannerMessages(prev, isChoiceRankBannerMessage)
            )
          }}
        />
        <p className="p2 -book text-sm md:text-base">
          {formatHours(Number.parseFloat(hoursPerDay) || 0)} Hours requested per
          day
        </p>
        <ActionButtons
          editMode
          onBack={() => {
            setEmbeddedEditMode(false)
            setShowHistoryTable(true)
          }}
          onCancel={() => setShowCancelModal(true)}
          onUpdate={handleUpdateBid}
        />

        {cancelConfirmationModal}
      </div>
    )
  }

  // Default: entry form for current selectedDates
  return (
    <div>
      {bannerMessages && bannerMessages.length > 0 && (
        <div className="space-y-3 mb-4">
          {bannerMessages.map((msg) => (
            <ValidationBanner
              key={msg}
              variant="error"
              heading={<p className="p1 -medium">{msg}</p>}
              compact
            />
          ))}
        </div>
      )}

      <AddModeHeader sortedDates={sortedDates} />

      <DurationInput
        hoursPerDay={hoursPerDay}
        onHoursChange={setHoursPerDay}
        error={hoursError}
        onErrorClear={() => {
          setHoursError('')
          setBannerMessages((prev) =>
            withoutBannerMessages(prev, isHoursBannerMessage)
          )
        }}
      />

      <ChoiceRankSelector
        selectedRank={selectedRank}
        onSelectRank={(opt) => {
          setSelectedRank(opt)
          setChoiceRankError('')
          setBannerMessages((prev) =>
            withoutBannerMessages(prev, isChoiceRankBannerMessage)
          )
        }}
        dropdownOptions={RANK_DROPDOWN_OPTIONS}
        error={choiceRankError}
        onErrorClear={() => {
          setChoiceRankError('')
          setBannerMessages((prev) =>
            withoutBannerMessages(prev, isChoiceRankBannerMessage)
          )
        }}
      />

      <p className="p2 -book text-sm md:text-base">
        {formatHours(Number.parseFloat(hoursPerDay) || 0)} Hours requested per
        day
      </p>
      <p className="p2 -book text-sm md:text-base">
        {formatHours(totalHours)} Hours Total
      </p>

      <ActionButtons
        editMode={false}
        onCancel={() => setShowCancelModal(true)}
        onAdd={handleAdd}
      />

      {cancelConfirmationModal}
    </div>
  )
}
